import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import myAxios from "../AxiosUrl";
import styles from "./TournamentDashboard.module.css";
import Loading from "../Loading/Loading";

const TournamentDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchDashboardData();
    // Auto refresh every 5 minutes
    const interval = setInterval(fetchDashboardData, 300000);
    return () => clearInterval(interval);
  }, []);

  const fetchDashboardData = async () => {
    try {
      setRefreshing(true);
      setError(null);

      const response = await myAxios.get("/dashboard/tournament");

      if (response.data.success) {
        setDashboardData(response.data.data);
        setLastRefresh(new Date());
      } else {
        setError("Failed to load dashboard data");
      }
    } catch (err) {
      setError("Failed to connect to server");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const formatNumber = (num) => {
    if (!num) return "0";
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      timeZone: "Europe/Istanbul",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  const getMatchStatus = (match) => {
    // Parse the match date properly
    const matchDate = new Date(match.date);
    const now = new Date();

    // Set time to start of day for date comparison
    const matchDateOnly = new Date(matchDate.getFullYear(), matchDate.getMonth(), matchDate.getDate());
    const nowDateOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    // Check match status field first
    if (match.status === "Live" || match.status === "live") {
      return { text: "LIVE", className: styles.live };
    }

    // Compare dates
    if (matchDateOnly > nowDateOnly) {
      // Future date - Upcoming
      return { text: "UPCOMING", className: styles.upcoming };
    } else if (matchDateOnly < nowDateOnly) {
      // Past date - Recent/Finished
      return { text: "RECENT", className: styles.recent };
    } else {
      // Same day - check if completed or live
      if (match.status === "completed" || match.status === "Result" || match.status === "FT") {
        return { text: "RECENT", className: styles.recent };
      }
      // Today but not started yet
      return { text: "TODAY", className: styles.upcoming };
    }
  };

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return (
      <div className={styles.dashboardContainer}>
        <div className={styles.dashboardContent}>
          <div className={styles.errorContainer}>
            <span className={`material-icons ${styles.errorIcon}`}>error_outline</span>
            <h2 className={styles.errorTitle}>Unable to Load Dashboard</h2>
            <p className={styles.errorMessage}>{error}</p>
            <button onClick={fetchDashboardData} className={styles.retryBtn}>
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  const {
    overview,
    fixtures,
    schedule,
    squads,
    standings,
    players,
    dataAvailability,
    recommendations,
  } = dashboardData || {};

  const getMetricChange = (value) => {
    if (!value) return { text: "0%", className: styles.neutral };
    if (value > 0) return { text: `+${value}%`, className: styles.positive, icon: "trending_up" };
    if (value < 0) return { text: `${value}%`, className: styles.negative, icon: "trending_down" };
    return { text: "0%", className: styles.neutral, icon: "trending_flat" };
  };

  return (
    <div className={styles.dashboardContainer}>
      <div className={styles.dashboardContent}>
        {/* Enhanced Header */}
        <div className={styles.dashboardHeader}>
          <div className={styles.headerTop}>
            <div>
              <h1 className={styles.dashboardTitle}>Tournament Dashboard</h1>
              <p className={styles.dashboardSubtitle}>
                Real-time tournament analytics and insights
              </p>
            </div>
            <div className={styles.refreshSection}>
              {lastRefresh && (
                <span className={styles.lastRefresh}>
                  <span className="material-icons">access_time</span>
                  Last updated: {lastRefresh.toLocaleTimeString("tr-TR")}
                </span>
              )}
              <button
                onClick={fetchDashboardData}
                className={`${styles.refreshBtn} ${refreshing ? styles.refreshing : ''}`}
                disabled={refreshing}
              >
                <span className="material-icons">refresh</span>
                {refreshing ? "Refreshing..." : "Refresh"}
              </button>
            </div>
          </div>
        </div>

        {/* Key Metrics Grid */}
        <div className={styles.keyMetricsGrid}>
          <motion.div
            className={`${styles.metricCard} ${styles.tournaments}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className={styles.metricHeader}>
              <div className={`${styles.metricIcon} ${styles.tournaments}`}>
                <span className="material-icons">emoji_events</span>
              </div>
              {overview?.tournamentChange && (
                <div className={`${styles.metricChange} ${getMetricChange(overview.tournamentChange).className}`}>
                  <span className="material-icons">
                    {getMetricChange(overview.tournamentChange).icon}
                  </span>
                  {getMetricChange(overview.tournamentChange).text}
                </div>
              )}
            </div>
            <div className={styles.metricValue}>
              {formatNumber(overview?.totalTournaments)}
            </div>
            <div className={styles.metricLabel}>Total Tournaments</div>
          </motion.div>

          <motion.div
            className={`${styles.metricCard} ${styles.fixtures}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            <div className={styles.metricHeader}>
              <div className={`${styles.metricIcon} ${styles.fixtures}`}>
                <span className="material-icons">schedule</span>
              </div>
              <div className={`${styles.metricChange} ${styles.positive}`}>
                <span className="material-icons">trending_up</span>
                {overview?.upcomingMatchesCount || 0} upcoming
              </div>
            </div>
            <div className={styles.metricValue}>
              {formatNumber(overview?.totalSchedules)}
            </div>
            <div className={styles.metricLabel}>Total Matches</div>
          </motion.div>

          <motion.div
            className={`${styles.metricCard} ${styles.players}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
          >
            <div className={styles.metricHeader}>
              <div className={`${styles.metricIcon} ${styles.players}`}>
                <span className="material-icons">person</span>
              </div>
              {players?.statistics?.totalActivePlayers && (
                <div className={`${styles.metricChange} ${styles.positive}`}>
                  <span className="material-icons">check_circle</span>
                  {formatNumber(players.statistics.totalActivePlayers)} active
                </div>
              )}
            </div>
            <div className={styles.metricValue}>
              {formatNumber(overview?.totalPlayers)}
            </div>
            <div className={styles.metricLabel}>Total Players</div>
          </motion.div>

          <motion.div
            className={`${styles.metricCard} ${styles.teams}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.3 }}
          >
            <div className={styles.metricHeader}>
              <div className={`${styles.metricIcon} ${styles.teams}`}>
                <span className="material-icons">groups</span>
              </div>
              <div className={`${styles.metricChange} ${styles.neutral}`}>
                <span className="material-icons">info</span>
                All teams
              </div>
            </div>
            <div className={styles.metricValue}>
              {formatNumber(overview?.totalTeams)}
            </div>
            <div className={styles.metricLabel}>Teams</div>
          </motion.div>

          <motion.div
            className={`${styles.metricCard} ${styles.competitions}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.4 }}
          >
            <div className={styles.metricHeader}>
              <div className={`${styles.metricIcon} ${styles.competitions}`}>
                <span className="material-icons">sports_soccer</span>
              </div>
              {overview?.liveMatchesCount > 0 && (
                <div className={`${styles.metricChange} ${styles.negative}`}>
                  <span className="material-icons">radio_button_checked</span>
                  {overview.liveMatchesCount} LIVE
                </div>
              )}
            </div>
            <div className={styles.metricValue}>
              {formatNumber(overview?.totalCompetitions)}
            </div>
            <div className={styles.metricLabel}>Competitions</div>
          </motion.div>

          <motion.div
            className={`${styles.metricCard} ${styles.upcomingMatches}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.5 }}
          >
            <div className={styles.metricHeader}>
              <div className={`${styles.metricIcon} ${styles.upcomingMatches}`}>
                <span className="material-icons">event</span>
              </div>
              <div className={`${styles.metricChange} ${styles.positive}`}>
                <span className="material-icons">schedule</span>
                Next 7 days
              </div>
            </div>
            <div className={styles.metricValue}>
              {formatNumber(overview?.upcomingMatchesCount)}
            </div>
            <div className={styles.metricLabel}>Upcoming Matches</div>
          </motion.div>
        </div>

        {/* Data Overview Grid */}
        <div className={styles.dataOverviewGrid}>
          {/* Recent Matches */}
          <div className={styles.overviewCard}>
            <div className={styles.overviewHeader}>
              <h3 className={styles.overviewTitle}>
                <span className="material-icons">sports_score</span>
                Recent & Upcoming Matches
              </h3>
              <button
                className={styles.viewAllBtn}
                onClick={() => window.location.href = "/TournamentSchedule"}
              >
                View All
              </button>
            </div>

            {schedule?.recentMatches?.length > 0 || schedule?.upcomingMatches?.length > 0 ? (
              <div className={styles.matchesList}>
                {[...((schedule?.recentMatches || []).slice(0, 3)),
                  ...((schedule?.upcomingMatches || []).slice(0, 3))].map((match, index) => {
                  const status = getMatchStatus(match);
                  return (
                    <div key={index} className={styles.matchItem}>
                      <div className={styles.matchTeams}>
                        <div className={`${styles.teamWrapper} ${styles.home}`}>
                          {match.homeTeam?.imageUrl && (
                            <img
                              src={match.homeTeam.imageUrl}
                              alt={match.homeTeam?.name}
                              className={styles.teamLogo}
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          )}
                          <span className={`${styles.teamName} ${styles.home}`}>
                            {match.homeTeam?.shortName || match.homeTeam?.name}
                          </span>
                        </div>
                        <span className={styles.matchScore}>
                          {match.score || <span className={styles.vs}>vs</span>}
                        </span>
                        <div className={`${styles.teamWrapper} ${styles.away}`}>
                          <span className={`${styles.teamName} ${styles.away}`}>
                            {match.awayTeam?.shortName || match.awayTeam?.name}
                          </span>
                          {match.awayTeam?.imageUrl && (
                            <img
                              src={match.awayTeam.imageUrl}
                              alt={match.awayTeam?.name}
                              className={styles.teamLogo}
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          )}
                        </div>
                      </div>
                      <div className={styles.matchMeta}>
                        <span className={styles.matchDate}>
                          {formatDate(match.date)}
                        </span>
                        <span className={`${styles.matchStatus} ${status.className}`}>
                          {status.text}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p>No match data available</p>
            )}
          </div>

          {/* Top Players */}
          <div className={styles.overviewCard}>
            <div className={styles.overviewHeader}>
              <h3 className={styles.overviewTitle}>
                <span className="material-icons">star</span>
                Top Players
              </h3>
              <button
                className={styles.viewAllBtn}
                onClick={() => window.location.href = "/TournamentPlayers"}
              >
                View All
              </button>
            </div>

            {players?.topPerformers?.length > 0 ? (
              <div className={styles.playersList}>
                {players.topPerformers.slice(0, 5).map((player, index) => (
                  <div key={index} className={styles.playerItem}>
                    <div className={styles.playerInfo}>
                      <div className={`${styles.playerRank} ${
                        index === 0 ? styles.gold :
                        index === 1 ? styles.silver :
                        index === 2 ? styles.bronze : ''
                      }`}>
                        {index + 1}
                      </div>
                      {player.profileImage && (
                        <img
                          src={player.profileImage}
                          alt={player.name}
                          className={styles.playerPhoto}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      )}
                      <div className={styles.playerDetails}>
                        <div className={styles.playerName}>{player.name}</div>
                        <div className={styles.playerTeam}>{player.club}</div>
                      </div>
                    </div>
                    <div className={styles.playerStats}>
                      <div className={styles.playerStat}>
                        <div className={styles.statValue}>{player.totalPoints}</div>
                        <div className={styles.statLabel}>PTS</div>
                      </div>
                      <div className={styles.playerStat}>
                        <div className={styles.statValue}>{player.goals}</div>
                        <div className={styles.statLabel}>G</div>
                      </div>
                      <div className={styles.playerStat}>
                        <div className={styles.statValue}>{player.assists}</div>
                        <div className={styles.statLabel}>A</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p>No player data available</p>
            )}
          </div>
        </div>

        {/* System Health Grid */}
        <div className={styles.overviewCard}>
          <div className={styles.overviewHeader}>
            <h3 className={styles.overviewTitle}>
              <span className="material-icons">monitor_heart</span>
              System Health
            </h3>
          </div>

          <div className={styles.systemHealthGrid}>
            <div className={styles.healthItem}>
              <div className={styles.healthInfo}>
                <div className={styles.healthLabel}>Database Connection</div>
                <div className={styles.healthValue}>
                  {overview?.systemHealth?.databaseConnection ? "Connected" : "Disconnected"}
                </div>
              </div>
              <div className={`${styles.healthStatus} ${
                overview?.systemHealth?.databaseConnection ? styles.good : styles.error
              }`}></div>
            </div>

            <div className={styles.healthItem}>
              <div className={styles.healthInfo}>
                <div className={styles.healthLabel}>API Status</div>
                <div className={styles.healthValue}>
                  {overview?.systemHealth?.apiConnectivity ? "Online" : "Limited"}
                </div>
              </div>
              <div className={`${styles.healthStatus} ${
                overview?.systemHealth?.apiConnectivity ? styles.good : styles.warning
              }`}></div>
            </div>

            <div className={styles.healthItem}>
              <div className={styles.healthInfo}>
                <div className={styles.healthLabel}>Data Coverage</div>
                <div className={styles.healthValue}>
                  {overview?.dataCompletenessScore || 0}% Complete
                </div>
              </div>
              <div className={`${styles.healthStatus} ${
                overview?.dataCompletenessScore >= 80 ? styles.good :
                overview?.dataCompletenessScore >= 50 ? styles.warning : styles.error
              }`}></div>
            </div>

            <div className={styles.healthItem}>
              <div className={styles.healthInfo}>
                <div className={styles.healthLabel}>Data Freshness</div>
                <div className={styles.healthValue}>
                  {overview?.systemHealth?.dataFreshness || "Unknown"}
                </div>
              </div>
              <div className={`${styles.healthStatus} ${styles.good}`}></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TournamentDashboard;